import { ResolvedMedia } from '@longy/common/cms'

import { cn } from '../../lib/utils'

export type CmsImageProps = {
    media: ResolvedMedia | null | undefined
    /** Always pass this for a content image: without it the browser assumes 100vw and over-fetches. */
    sizes?: string
    /** The above-the-fold image only. More than one per page defeats the purpose. */
    priority?: boolean
    alt?: string
    className?: string
    draggable?: boolean
}

/**
 * The one image element in the system, so every image gets the same behaviour rather than each
 * renderer remembering it.
 *
 * The intrinsic width and height are the whole reason the api reads them off the uploaded buffer:
 * they let the browser reserve the box before the bytes arrive, which is the entire
 * cumulative-layout-shift story.
 */
export const CmsImage = ({
    media,
    sizes,
    priority = false,
    alt,
    className,
    draggable,
}: CmsImageProps) => {
    if (!media?.url) return null

    return (
        <img
            src={media.url}
            srcSet={media.srcset ?? undefined}
            sizes={media.srcset ? sizes : undefined}
            alt={alt ?? media.altText ?? ''}
            width={media.width ?? undefined}
            height={media.height ?? undefined}
            loading={priority ? 'eager' : 'lazy'}
            decoding={priority ? 'auto' : 'async'}
            fetchPriority={priority ? 'high' : undefined}
            draggable={draggable}
            className={cn('max-w-full', className)}
        />
    )
}

export type CmsPictureProps = Omit<CmsImageProps, 'media'> & {
    desktop: ResolvedMedia | null | undefined
    mobile?: ResolvedMedia | null
    mobileMaxWidth?: number
}

/**
 * Art direction composes on top of `CmsImage` rather than replacing it, so each crop keeps its own
 * resolution switching. No source element is emitted when the two crops are the same image.
 */
export const CmsPicture = ({
    desktop,
    mobile,
    mobileMaxWidth = 767,
    ...imageProps
}: CmsPictureProps) => {
    if (!desktop?.url) return <CmsImage media={mobile} {...imageProps} />

    const hasDistinctMobile = Boolean(mobile?.url && mobile.url !== desktop.url)

    return (
        <picture>
            {hasDistinctMobile && mobile && (
                <source
                    media={`(max-width: ${mobileMaxWidth}px)`}
                    srcSet={mobile.srcset ?? mobile.url}
                    sizes={mobile.srcset ? imageProps.sizes : undefined}
                    width={mobile.width ?? undefined}
                    height={mobile.height ?? undefined}
                />
            )}
            <CmsImage media={desktop} {...imageProps} />
        </picture>
    )
}
